import {
  Box,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  IconButton,
  Menu,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import Header from "../components/Admin/header/Header";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import { Search, X } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useGetApi } from "../hooks/useGetApi";
import { getAppointmentApi, deleteAppointmentByIdApi } from "../services/appointment.service";
import { deleteConsultationByIdApi } from "../services/consultation.service";
import {
  CAPITALIZE,
  DEFAULT_LIMIT,
  DEFAULT_ROWS_PER_PAGE_OPTIONS,
} from "../utils/constants";
import { AddCircleOutlineOutlined, MoreVert } from "@mui/icons-material";
import Loader from "../components/Loader/loader";
import ErrorMessage from "../components/ErrorMessage/errorMessage";
import dayjs from "dayjs";
import { getUnitApi } from "../services/unit.service";
import { DatePicker } from "@mui/x-date-pickers";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { hasPermission } from "../utils/permissionHelper";

const popoverOptions = ["Add Consultation", "Reschedule", "Bill", "Cancel", "Delete"];

const buttons = [
  { label: "All", value: "all", width: "110px" },
  { label: "Scheduled", value: "scheduled", width: "140px" },
  { label: "Re-Scheduled", value: "re-scheduled", width: "170px" },
  { label: "Cancelled", value: "cancelled", width: "140px" },
];

const Appointments = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));
  const user = useSelector((state) => state.user);

  const [search, setSearch] = useState("");
  const [searchBy, setSearchBy] = useState("doctorName");
  const [status, setStatus] = useState(buttons?.[0]?.value);
  const [unit, setUnit] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: DEFAULT_LIMIT,
  });
  const [sortModel, setSortModel] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorPosition, setAnchorPosition] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchContainerRef = useRef(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setIsSearchFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Helper function to get available options based on appointment status
  const getAvailableOptions = (appointmentStatus) => {
    if (appointmentStatus === "cancelled" || appointmentStatus === "converted") {
      // Only show Delete option for cancelled and converted appointments
      return ["Delete"];
    }
    // Show all options for other statuses (scheduled, re-scheduled, etc.)
    return popoverOptions;
  };

  // Filter popover options based on user permissions and appointment status
  const getFilteredPopoverOptions = (appointmentStatus) => {
    const availableOptions = getAvailableOptions(appointmentStatus);
    
    const filteredOptions = availableOptions.filter(option => {
      if (option === "Delete") {
        return hasPermission(user, "Appointments.Delete");
      }
      // Add other permission checks here if needed
      return true;
    });
    
    // If no options are available due to permissions, show at least some basic options
    if (filteredOptions.length === 0 && appointmentStatus !== "cancelled" && appointmentStatus !== "converted") {
      return ["Add Consultation", "Reschedule", "Bill", "Cancel"];
    }
    
    return filteredOptions;
  };

  function calculateAge(dob) {
    const birthDate = new Date(dob);
    const today = new Date();
    return Math.floor((today - birthDate) / (1000 * 60 * 60 * 24 * 365.25));
  }

  const handleNewPatient = () => {
    navigate("/patient/form");
  };

  const handlePopoverClick = (option) => {
    if (option === "Add Consultation") {
      navigate(`/patient/convert-consultation-opd/${selectedRow?._id}`);
      setAnchorEl(null);
    } else if (option === "Reschedule") {
      navigate(`${selectedRow?._id}/reschedule`);
      setAnchorEl(null);
    } else if (option === "Bill") {
      navigate(`/billing/${selectedRow?.patient?._id}`);
      setAnchorEl(null);
    } else if (option === "Cancel") {
      navigate(`${selectedRow?._id}/cancel`);
      setAnchorEl(null);
    } else if (option === "Delete") {
      // Don't clear selectedRow here - keep it for delete operation
      setDeleteDialogOpen(true);
      setAnchorEl(null);
    }
  };

  const handleRightClick = (event, row) => {
    const availableOptions = getFilteredPopoverOptions(row?.status);
    if (availableOptions.length === 0) {
      return;
    }
    event.preventDefault(); // Prevent the default context menu
    setAnchorEl(event.currentTarget);
    setAnchorPosition({
      top: event.clientY,
      left: event.clientX,
    });
    setSelectedRow(row); // Set the clicked row data
  };

  const handleClick = (event, row) => {
    const availableOptions = getFilteredPopoverOptions(row?.status);
    
    // Always open the menu to see what's happening
    setAnchorEl(event.currentTarget);
    setAnchorPosition(null);
    setSelectedRow(row); // Set the clicked row data
    
    // Show toast only if truly no options (but still open menu for debugging)
    if (availableOptions.length === 0) {
      toast.error("No actions available for this appointment");
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  // Helper function to find consultation by appointment ID
  const findConsultationByAppointmentId = async (appointmentId) => {
    try {
      // Use the existing consultation API to search by convertFrom field
      // Since we don't have a direct API for this, we'll handle it in the backend or
      // make a request to get consultations and filter by convertFrom
      // For now, we'll return the appointmentId to be handled by backend
      return appointmentId;
    } catch (error) {
      console.error("Error finding consultation:", error);
      return null;
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedRow) return;
    
    setIsDeleting(true);
    try {
      const appointmentId = selectedRow._id;
      const appointmentStatus = selectedRow.status;

      console.log(`Deleting appointment ${appointmentId} with status: ${appointmentStatus}`);

      // Delete the appointment (backend now handles cascade deletion for converted appointments)
      const response = await deleteAppointmentByIdApi(appointmentId);
      
      if (response.code === 200) {
        // Use the message from backend which indicates if consultation was also deleted
        toast.success(response.message || "Appointment deleted successfully!");
        
        // Refresh the appointments list by refetching
        window.location.reload(); // Simple refresh for now
        setDeleteDialogOpen(false);
        setSelectedRow(null);
      } else {
        toast.error(response.message || "Failed to delete appointment");
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("An error occurred while deleting the appointment");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedRow(null);
  };

  const handlePaginationModelChange = (newModel) => {
    setPaginationModel(newModel);
  };

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
  };

  const {
    dataList: appointmentList,
    isLoading,
    isError,
  } = useGetApi({
    apiFunction: getAppointmentApi,
    params: {
      limit: 10000,
      page: 1,
      search,
      searchBy,
      status,
      unit,
      dateFilter,
      ...(dateFilter === "range" && { fromDate, toDate }),
    },
    dependencies: [
      search,
      searchBy,
      status,
      unit,
      dateFilter,
      fromDate,
      toDate,
    ],
    debounceDelay: 500,
    dataKey: "appointments",
    countKey: "total",
  });

  console.log("isLoading", isLoading, "isError", isError, "appointmentList", appointmentList);

  // Check if any appointment has available actions - if not, hide the Actions column
  const hasActionsForAnyRow = appointmentList?.some(row => 
    getFilteredPopoverOptions(row?.status)?.length > 0
  ) || false;

  // Prepare rows for DataGrid (client-side sorting/pagination)
  const rows = appointmentList?.map((row) => ({
    id: row._id,
    _id: row._id,
    UHID: row?.UHID ?? row?.patient?.UHID ?? "N/A",
    patientName: `${row?.patient?.firstName || ""} ${row?.patient?.lastName || ""}`.trim(),
    appointmentDateTime: row?.appointmentDateTime,
    age: calculateAge(row?.patient?.dob),
    gender: row?.patient?.gender,
    contact: row?.patient?.phone ?? "N/A",
    doctorName: row?.doctor ? `${row.doctor.firstName} ${row.doctor.lastName}` : "N/A",
    status: row?.status,
    patient: row?.patient,
    doctor: row?.doctor,
  })) ?? [];

  // Compute status counts when viewing "all" appointments
  const statusCountsRef = useRef({});
  useMemo(() => {
    if (status === "all" && appointmentList?.length > 0) {
      const counts = { all: appointmentList.length };
      appointmentList.forEach((row) => {
        const s = row?.status;
        if (s) counts[s] = (counts[s] || 0) + 1;
      });
      statusCountsRef.current = counts;
    }
  }, [status, appointmentList]);

  const getColumns = useCallback(() => {
    const baseColumns = [
      { field: "UHID", headerName: "UHID", flex: isMobile ? 0 : 0.8, width: isMobile ? 100 : undefined, minWidth: 90, headerAlign: "center", align: "center", sortable: true },
      {
        field: "patientName",
        headerName: "Patient Name",
        flex: isMobile ? 0 : 1.2,
        width: isMobile ? 120 : undefined,
        minWidth: 120,
        headerAlign: "center",
        align: "center",
        sortable: true,
        renderCell: (params) => (
          <Box
            component={Link}
            to={`/patient/info/${params.row.patient?._id}`}
            sx={{
              color: "primary.main",
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            {params.value || "—"}
          </Box>
        ),
      },
      {
        field: "appointmentDateTime",
        headerName: "Appointment Date",
        flex: isMobile ? 0 : 1,
        width: isMobile ? 110 : undefined,
        minWidth: 110,
        headerAlign: "center",
        align: "center",
        sortable: true,
        valueFormatter: (params) => (params.value ? dayjs(params.value).format("DD/MM/YYYY, hh:mm A") : "—"),
      },
      { field: "age", headerName: "Age", flex: isMobile ? 0 : 0.5, width: isMobile ? 60 : undefined, minWidth: 50, headerAlign: "center", align: "center", sortable: true },
      { field: "gender", headerName: "Gender", flex: isTablet ? 0 : 0.7, width: isTablet ? 80 : undefined, minWidth: 70, headerAlign: "center", align: "center", sortable: true, valueFormatter: (params) => (params.value ? CAPITALIZE(params.value) : "—") },
      { field: "contact", headerName: "Contact Number", flex: isMobile ? 0 : 1, width: isMobile ? 110 : undefined, minWidth: 110, headerAlign: "center", align: "center", sortable: true },
      { field: "doctorName", headerName: "Doctor Consulted", flex: isTablet ? 0 : 1, width: isTablet ? 120 : undefined, minWidth: 120, headerAlign: "center", align: "center", sortable: true },
      {
        field: "status",
        headerName: "Status",
        flex: isTablet ? 0 : 0.8,
        width: isTablet ? 90 : undefined,
        minWidth: 80,
        headerAlign: "center",
        align: "center",
        sortable: true,
        renderCell: (params) => (
          <Box component="span" sx={{ color: params.value === "cancelled" ? "red" : "black" }}>
            {params.value ? CAPITALIZE(params.value) : "—"}
          </Box>
        ),
      },
    ];
    if (hasActionsForAnyRow) {
      baseColumns.push({
        field: "actions",
        headerName: "Actions",
        flex: 0,
        width: 70,
        minWidth: 60,
        headerAlign: "center",
        align: "center",
        sortable: false,
        renderCell: (params) =>
          getFilteredPopoverOptions(params.row?.status)?.length > 0 ? (
            <IconButton size={isMobile ? "small" : "medium"} onClick={(e) => { e.stopPropagation(); handleClick(e, params.row); }} sx={{ cursor: "pointer" }}>
              <MoreVert />
            </IconButton>
          ) : null,
      });
    }
    return baseColumns;
  }, [isMobile, isTablet, hasActionsForAnyRow]);

  const { dataList: unitList } = useGetApi({
    apiFunction: getUnitApi,
    params: { limit: Number.MAX_SAFE_INTEGER },
    dataKey: "units",
  });

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box sx={{}}>
          <Sidebar />
        </Box>
        <Box sx={{ 
          width: "100%", 
          pr: { xs: "10px", sm: "20px", md: "40px", lg: "60px", xl: "90px" },
          pl: { xs: "10px", sm: "20px", md: "20px" },
          maxWidth: "100vw",
          overflow: "hidden"
        }}>
          {/* Search */}
          <Box
            sx={{
              marginTop: "20px",
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "40px",
            }}
          >
            <Box
              ref={searchContainerRef}
              sx={{
                display: "flex",
                backgroundColor: "white",
                border: isSearchFocused ? "2px solid #0179B0" : "1px solid #e0e0e0",
                borderRadius: "8px",
                alignItems: "center",
                position: "relative",
                boxShadow: isSearchFocused 
                  ? "0 4px 12px rgba(1, 121, 176, 0.15)" 
                  : "0 2px 4px rgba(0, 0, 0, 0.05)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                  borderColor: "#c0c0c0"
                }
              }}
            >
              {/* Dropdown - only show when search is focused */}
              {isSearchFocused && (
                <select
                  onChange={(e) => setSearchBy(e.target.value)}
                  value={searchBy}
                  style={{
                    border: "none",
                    outline: "none",
                    background: "transparent",
                    padding: "10px",
                    fontSize: "14px",
                    width: `${Math.max(120, (searchBy === "doctorName" ? "Doctor" : 
                      searchBy === "patientName" ? "Patient Name" : 
                      searchBy === "contactNo" ? "Contact Number" : "Doctor").length * 9 + 30)}px`,
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    borderRight: "1px solid #e0e0e0",
                    color: "#333",
                    fontWeight: "500",
                    touchAction: "manipulation"
                  }}
                >
                  <option value="doctorName">Doctor</option>
                  <option value="patientName">Patient Name</option>
                  <option value="contactNo">Contact Number</option>
                </select>
              )}
              
              {/* Search Input */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  padding: "12px 16px",
                  width: "300px",
                  transition: "all 0.3s ease",
                  gap: "10px"
                }}
              >
                <Search 
                  size={20} 
                  style={{ 
                    color: isSearchFocused ? "#0179B0" : "#666",
                    transition: "color 0.3s ease",
                    flexShrink: 0
                  }} 
                />
                <input
                  type="text"
                  placeholder={`Search by ${searchBy === "doctorName" ? "Doctor" : 
                    searchBy === "patientName" ? "Patient Name" : 
                    searchBy === "contactNo" ? "Contact Number" : "Doctor"}`}
                  value={search || ""}
                  onChange={(e) => setSearch(e.target.value)}
                  onFocus={() => setIsSearchFocused(true)}
                  style={{
                    border: "none",
                    outline: "none",
                    background: "transparent",
                    width: "100%",
                    fontSize: "14px",
                    color: "#333",
                    padding: "0",
                    minHeight: "24px",
                    touchAction: "manipulation",
                    WebkitTapHighlightColor: "transparent"
                  }}
                />
                {search && (
                  <X 
                    size={18} 
                    style={{ 
                      cursor: "pointer", 
                      color: "#999",
                      flexShrink: 0,
                      padding: "2px",
                      transition: "all 0.2s ease",
                      touchAction: "manipulation"
                    }}
                    onClick={() => setSearch("")}
                    onMouseEnter={(e) => e.target.style.color = "#0179B0"}
                    onMouseLeave={(e) => e.target.style.color = "#999"}
                  />
                )}
              </Box>
            </Box>
            <Box>
              <Button
                variant="contained"
                sx={{ textTransform: "none" }}
                onClick={handleNewPatient}
              >
                <AddCircleOutlineOutlined sx={{ mr: 0.5 }} />
                Patient Registration
              </Button>
            </Box>
          </Box>

          {/* Filter */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "40px",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <FormControl size="small" sx={{ minWidth: "100px" }}>
                <InputLabel id="date-select-label">Date</InputLabel>
                <Select
                  labelId="date-select-label"
                  label="Date"
                  value={dateFilter || ""}
                  onChange={(e) => setDateFilter(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="yesterday">Yesterday</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                  <MenuItem value="yearly">Yearly</MenuItem>
                  <MenuItem value="range">Range</MenuItem>
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: "100px" }}>
                <InputLabel id="unit-label">Unit</InputLabel>
                <Select
                  labelId="unit-label"
                  label="Unit"
                  value={unit || ""}
                  onChange={(e) => setUnit(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  {unitList?.map((option) => (
                    <MenuItem value={option?._id} key={option?._id}>
                      {option?.unitName || ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Show date range pickers if "Range" is selected */}
              {dateFilter === "range" && (
                <>
                  <DatePicker
                    label="From Date"
                    value={fromDate ? dayjs(fromDate, "YYYY-MM-DD") : null}
                    onChange={(newValue) =>
                      setFromDate(
                        newValue ? dayjs(newValue).format("YYYY-MM-DD") : null
                      )
                    }
                    slotProps={{ textField: { size: "small" } }}
                    sx={{ width: "150px" }}
                  />
                  <DatePicker
                    label="To Date"
                    value={toDate ? dayjs(toDate, "YYYY-MM-DD") : null}
                    onChange={(newValue) =>
                      setToDate(
                        newValue ? dayjs(newValue).format("YYYY-MM-DD") : null
                      )
                    }
                    slotProps={{ textField: { size: "small" } }}
                    sx={{ width: "150px" }}
                  />
                </>
              )}
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {buttons?.map((button) => (
                <Button
                  key={button?.value}
                  variant="outlined"
                  onClick={() => setStatus(button?.value)}
                  sx={{
                    border: `2px solid ${
                      status === button?.value
                        ? theme.palette.primary.dark
                        : "#c0c0c0"
                    }`,
                    color:
                      status === button?.value
                        ? theme.palette.primary.dark
                        : "text.secondary",
                    bgcolor: status === button?.value ? "#AED5F3" : "#F0F7FC",
                    fontWeight: status === button?.value ? 600 : 400,
                    width: button?.width || "100px",
                    whiteSpace: "nowrap",
                    textTransform: "none",
                  }}
                >
                  {button?.label || ""}{statusCountsRef.current[button?.value] !== undefined ? ` (${statusCountsRef.current[button?.value]})` : ""}
                </Button>
              ))}
            </Box>
          </Box>

          {/* DataGrid Table */}
          {isLoading ? (
            <Loader />
          ) : isError ? (
            <ErrorMessage />
          ) : (
            <>
              <Box>
                <Box
                  sx={{
                    height: { xs: "calc(100vh - 300px)", sm: "calc(100vh - 320px)", md: "calc(100vh - 350px)" },
                    width: "100%",
                    minHeight: "300px",
                    maxWidth: "100%",
                    overflow: "hidden",
                  }}
                >
                  <DataGrid
                    rows={rows}
                    columns={getColumns()}
                    paginationModel={paginationModel}
                    onPaginationModelChange={handlePaginationModelChange}
                    pageSizeOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
                    paginationMode="client"
                    sortingMode="client"
                    sortModel={sortModel}
                    onSortModelChange={handleSortModelChange}
                    disableRowSelectionOnClick
                    loading={isLoading}
                    sx={{
                      border: "none",
                      "& .MuiDataGrid-root": { border: "none", width: "100%" },
                      "& .MuiDataGrid-main": { width: "100%" },
                      "& .MuiDataGrid-cell": {
                        borderBottom: "1px solid #f0f0f0",
                        padding: isMobile ? "4px" : "8px",
                        fontSize: isMobile ? "12px" : "14px",
                      },
                      "& .MuiDataGrid-columnHeaders": { backgroundColor: "#f5f5f5", borderBottom: "2px solid #e0e0e0" },
                      "& .MuiDataGrid-columnHeader": { backgroundColor: "#f5f5f5", fontWeight: "bold", fontSize: isMobile ? "12px" : "14px" },
                      "& .MuiDataGrid-columnHeaderTitle": { fontWeight: "bold", fontSize: isMobile ? "12px" : "14px" },
                      "& .MuiDataGrid-row": {
                        "&:hover": { backgroundColor: "#f5f5f5", cursor: "pointer" },
                        "&:nth-of-type(odd)": { backgroundColor: "#fafafa" },
                        "&:nth-of-type(even)": { backgroundColor: "white" },
                      },
                      "& .MuiDataGrid-footerContainer": { borderTop: "2px solid #e0e0e0", backgroundColor: "#f9f9f9" },
                      "& .MuiDataGrid-virtualScroller": { minHeight: "200px" },
                      [theme.breakpoints.down("sm")]: {
                        "& .MuiDataGrid-columnHeader": { fontSize: "11px", padding: "4px" },
                        "& .MuiDataGrid-cell": { fontSize: "11px", padding: "2px 4px" },
                      },
                    }}
                    componentsProps={{
                      row: {
                        onContextMenu: (event) => {
                          event.preventDefault();
                          const rowId = event.currentTarget.getAttribute("data-id");
                          const rowData = rows.find((r) => String(r.id) === rowId);
                          if (rowData && getFilteredPopoverOptions(rowData?.status)?.length > 0) {
                            handleRightClick(event, rowData);
                          }
                        },
                      },
                    }}
                    components={{
                      NoRowsOverlay: () => (
                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                          <Typography variant="body2" color="text.secondary">No appointments found</Typography>
                        </Box>
                      ),
                    }}
                  />
                </Box>
              </Box>

              {/* Pop-up Menu */}

              <Menu
                id={Boolean(anchorEl) ? "row-popover" : undefined}
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                anchorReference={anchorPosition ? "anchorPosition" : "anchorEl"}
                anchorPosition={
                  anchorPosition
                    ? { top: anchorPosition.top, left: anchorPosition.left }
                    : undefined
                }
                anchorOrigin={{
                  vertical: "center",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                slotProps={{
                  paper: {
                    sx: {
                      borderRadius: "8px",
                      boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
                      maxHeight: "300px",
                    },
                  },
                }}
              >
                {selectedRow ? (
                  getFilteredPopoverOptions(selectedRow?.status)?.map((option) => (
                    <MenuItem key={option} onClick={() => handlePopoverClick(option)}>
                      {option}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>Loading...</MenuItem>
                )}
              </Menu>
            </>
          )}
        </Box>
      </Box>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirm Deletion"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this appointment?
            <br/><br/>
            <strong>This will also delete:</strong>
            <ul style={{ marginLeft: '20px', marginTop: '10px' }}>
              <li>Associated bills/invoices</li>
              <li>Payment records</li>
              <li>Billing account entries</li>
              {selectedRow?.status === "converted" && (
                <li><strong>Associated consultation</strong> (since this appointment was converted)</li>
              )}
            </ul>
            <br/>This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={isDeleting}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            disabled={isDeleting}
            autoFocus
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Appointments;
